/**
 * Technology Actions Toolbar Component
 *
 * Migrated to use shared TableActionsBar component for consistency.
 *
 * Displays action toolbar when technologies are selected, providing:
 * - Annotate dropdown (note, tag, remove tag)
 * - Share button (share URLs)
 * - Export button (always enabled, right-aligned)
 *
 * This is the simplest action bar configuration (no Manage or Scan actions).
 */

import {
  buildAnnotateAction,
  buildExportAction,
  buildShareAction,
  TableActionsBar,
} from '@/components/table/actions';
import type { ActionHandlers } from '@/sections/technology/types/action.types';
import type { ModalHandlers } from '@/sections/technology/types/modal.types';
import type { Technology } from '@/types';

/**
 * Props for TechnologyActions component
 */
export interface TechnologyActionsProps {
  /** Currently selected technologies */
  selectedTechnologies: Technology[];
  /** Number of selected rows */
  selectedRowsCount: number;
  /** Modal open/close handlers */
  modalHandlers: ModalHandlers;
  /** Action handlers for bulk operations */
  actionHandlers: ActionHandlers;
  /** Handler for clearing selection */
  onClearSelection: () => void;
  /** Hide all action buttons */
  hideActions?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Action toolbar component for bulk technology operations
 *
 * Provides configuration-based actions:
 * - LEFT (selection-dependent): Annotate, Share
 * - RIGHT (always-enabled): Export
 *
 * Uses shared TableActionsBar component for consistency with Seeds, Assets,
 * and Vulnerabilities sections.
 *
 * Example usage:
 * ```tsx
 * <TechnologyActions
 *   selectedTechnologies={selectedTechnologies}
 *   selectedRowsCount={selectedKeys.length}
 *   modalHandlers={modalHandlers}
 *   actionHandlers={handlers}
 *   onClearSelection={clearSelection}
 *   hideActions={!isRowsSelected}
 * />
 * ```
 *
 * @param props - Component props
 * @returns Toolbar component with shared TableActionsBar
 */
export function TechnologyActions({
  selectedTechnologies,
  selectedRowsCount,
  modalHandlers,
  actionHandlers,
  hideActions,
  className,
}: TechnologyActionsProps) {
  // Disabled state for actions (when no rows selected)
  const isDisabled = selectedRowsCount === 0;

  // Build action configurations using shared builders
  const actions = [
    buildAnnotateAction({
      onCommentClick: modalHandlers.openNoteModal,
      onTagClick: modalHandlers.openTagModal,
      onRemoveTagClick: modalHandlers.openRemoveTagModal,
      labels: { comment: 'Add Note' },
      testId: 'technology-annotate-dropdown',
    }),

    buildShareAction({
      variant: 'button-only',
      onShareClick: actionHandlers.handleShare || (() => {}),
      testId: 'technology-share-button',
    }),

    buildExportAction({
      onExportClick: modalHandlers.openExportModal,
      disabled: false, // Export always enabled
      testId: 'technology-export-button',
    }),
  ];

  return (
    <TableActionsBar
      selectedItems={selectedTechnologies}
      actions={actions}
      disabled={isDisabled}
      hideActions={hideActions}
      className={className}
      testIdPrefix="technology"
    />
  );
}
